/**
 * ClientProgressChart
 * Barres verticales groupées de progression par client (US done vs total)
 */

import React from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users } from 'lucide-react';
import { computeClientProgressData } from '../../utils/dashboardMetrics';

const COLORS = ['#6B4C9A','#10B981','#F59E0B','#3B82F6','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316','#84CC16'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 min-w-[160px]">
      <p className="font-semibold text-gray-800 text-sm mb-1 truncate">{d.clientName}</p>
      <p className="text-xs text-purple-700 font-bold">{d.progressPct}% réalisé</p>
      <p className="text-xs text-gray-500 mt-0.5">{d.doneUS}/{d.totalUS} US · {d.doneSP}/{d.totalSP} SP</p>
    </div>
  );
};

const ClientProgressChart = ({ usStageGroups = [], projects = [], sprintActif = false, colorMap = {} }) => {
  const data = computeClientProgressData(usStageGroups, projects, sprintActif);

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center justify-center min-h-[200px]">
        <Users className="w-10 h-10 text-gray-200 mb-3" />
        <p className="text-sm text-gray-400">Aucune donnée client disponible</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
          <Users className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">
            Progression par client{sprintActif ? ' — Sprint actif' : ''}
          </h3>
          <p className="text-xs text-gray-500">
            {data.length} client{data.length > 1 ? 's' : ''} · % US réalisées
          </p>
        </div>
      </div>

      <div style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="clientName"
              tick={{ fontSize: 11, fill: '#374151' }}
              axisLine={false}
              tickLine={false}
              angle={-35}
              textAnchor="end"
              interval={0}
              tickFormatter={v => v.length > 16 ? v.slice(0, 16) + '…' : v}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              tickFormatter={v => `${v}%`}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="progressPct" radius={[4, 4, 0, 0]}>
              {data.map((d, i) => <Cell key={i} fill={colorMap[d.clientName] || COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ClientProgressChart;
